import React, { ReactElement, FC, useRef } from 'react';
import styled, { css } from 'styled-components';
import loadable from '@loadable/component';
import { forTablet, forWideScreens } from '@westwing/ui-kit/breakPoints';
import { FormattedInfusionData } from '../../../redux/infusions/types';
import { ProductList } from '../../../../common/ga/types';
import ErrorBoundary from '../../Generic/ErrorBoundaries';
import GenericErrorRetry from '../../Generic/ErrorPages/GenericErrorRetry';
import { LooksInfusion, ProductInterface, WishlistTrackingFn } from '../../../redux/products/types';
import { GenericProductProps } from '../../Product/GenericProduct';
import Infusions from '../Infusions';
import normalizeGaProduct from '../../../../common/ga/normalize';
import useRouter from '../../../hooks/useRouter';
import useFeatureToggle from '../../../hooks/useFeatureToggle';
import useGa from '../../../hooks/useGa';
import { HorizontalGridProduct, StyledGrid, StyledHorizontalGrid, Title, StyledLineSeparator } from './ProductGrid';
import FormattedProduct from './FormattedProduct';
import useBellaTranslator from 'Client/hooks/useTranslator';
import useConfig from 'Client/hooks/useConfig';
import { useBellaSelector } from 'Client/redux/hooks';

const SkuDiverActionMenu = loadable(() => import('../SkuDiver/SkuDiverActionMenu'));

/**
 * This array contains the position starting from '0' in which we should have the Hero Product
 */
export const heroProductsSpotsInGrid: number[] = [6, 15, 26, 33, 42, 53];

export const noMobileInfusionZone: number[] = heroProductsSpotsInGrid.reduce((acc: number[], pos) => {
    for (let i = pos - 2; i <= pos + 2; i++) {
        acc.push(i);
    }
    return acc;
}, []);

interface GridBaseProps {
    className?: string;
    gridConfiguration?: number[];
    products: ProductInterface[];
    wishlistProducts?: string[];
    isInline?: boolean;
    list?: ProductList;
    infusions?: FormattedInfusionData;
    eagerImageLoading?: boolean;
    ref?: React.RefObject<HTMLDivElement> | null;
    looksInfusions?: LooksInfusion[] | null;
}

export interface ProductGridProps extends GridBaseProps {
    alternativeProducts?: ProductInterface[];
}

const HorizontalGridWrapper = styled(StyledHorizontalGrid)<{ sliderLength: number }>`
    margin-top: 1.4em;
    padding-left: 2.15em;
    padding-bottom: 40px;
    ${({ sliderLength }) => css`
        ${forTablet`
            padding-left: 0;
            grid-template-columns: repeat(${sliderLength}, 200px);
        `}
        ${forWideScreens`
            grid-template-columns: repeat(${sliderLength}, 303px);
        `}
    `}

    &::after {
        display: block;
        content: '';
        height: 100%;
        width: 1px;
    }
`;

export const isHeroProduct = (position: number): boolean => heroProductsSpotsInGrid.includes(position);

interface GetGridProductInterface extends GenericProductProps {
    isInline?: boolean;
    index: number;
    list?: ProductList;
    eagerImageLoading?: boolean;
    looksInfusions?: LooksInfusion[] | null;
    products?: ProductInterface[];
    desktopInfusionCounter?: React.MutableRefObject<number>;
    mobileInfusionCounter?: React.MutableRefObject<number>;
}

// eslint-disable-next-line complexity
const GridProduct = ({
    isInline = false,
    eagerImageLoading = false,
    ...productProps
}: GetGridProductInterface): ReactElement => {
    const { product, looksInfusions, mobileInfusionCounter, desktopInfusionCounter, list } = productProps;
    const {
        host,
        location: { pathname },
    } = useRouter();

    const { position } = product;

    const gaTracking = useGa();
    const { currency } = useConfig();

    const absoluteUrl = `${host}${pathname}`;
    const isHero = isHeroProduct(position);
    const defaultTrackingFn = () => {
        gaTracking.trackEcProductClick(
            normalizeGaProduct(product, {
                list,
            })
        );
        if (isHero) {
            gaTracking.trackECommerce({
                action: gaTracking.actions.ECommerce.HeroProductClick,
                label: pathname,
            });
        }
    };
    const defaultWishListClickTrackingFn: WishlistTrackingFn = ({ action, sku, category, name, brand, price }) => {
        gaTracking.trackWishListHeartClick({
            action,
            label: `PLP,${sku}`,
            sku,
            category,
            name,
            brand,
            price,
            currency,
            url: absoluteUrl,
        });
    };

    const productWithTracking = {
        ...productProps.product,
        clickTrackingFn: productProps.product.clickTrackingFn || defaultTrackingFn,
        wishListTrackingFn: productProps.product.wishListTrackingFn || defaultWishListClickTrackingFn,
    };

    // load eagerly images in the first row
    const imageLoading: 'eager' | 'lazy' = eagerImageLoading && position < 3 ? 'eager' : 'lazy';

    const props = {
        ...productProps,
        product: productWithTracking,
        imageLoading,
    };

    if (isInline) {
        return <HorizontalGridProduct key={`${productProps.index}/${productProps.product.simpleSku}`} {...props} />;
    }

    return (
        <FormattedProduct
            looksInfusions={looksInfusions}
            desktopInfusionCounter={desktopInfusionCounter}
            mobileInfusionCounter={mobileInfusionCounter}
            isHero={isHero}
            {...props}
        />
    );
};

export const ProductsGrid: FC<GridBaseProps> = React.memo(
    ({ products, isInline, list, infusions, eagerImageLoading = false }) => {
        return (
            <>
                <ErrorBoundary boundaryName="ProductGrid" fallbackComponent={<GenericErrorRetry />}>
                    {products.map((product, index) => (
                        <GridProduct
                            product={product}
                            key={product.simpleSku}
                            index={index}
                            isInline={isInline}
                            list={list}
                            eagerImageLoading={eagerImageLoading}
                        />
                    ))}
                </ErrorBoundary>
                {infusions && <Infusions infusions={infusions} />}
            </>
        );
    }
);

export const PLPProductsGrid: FC<GridBaseProps> = ({
    products,
    isInline,
    list,
    infusions,
    eagerImageLoading = false,
    looksInfusions,
}) => {
    const desktopInfusionCounter = useRef(0) as React.MutableRefObject<number>;
    const mobileInfusionCounter = useRef(0) as React.MutableRefObject<number>;
    mobileInfusionCounter.current = 0;
    desktopInfusionCounter.current = 0;

    return (
        <>
            <ErrorBoundary boundaryName="ProductGrid" fallbackComponent={<GenericErrorRetry />}>
                {products.map((product, index) => (
                    <GridProduct
                        product={product}
                        products={products}
                        key={product.simpleSku}
                        index={index}
                        isInline={isInline}
                        list={list}
                        eagerImageLoading={eagerImageLoading}
                        looksInfusions={looksInfusions}
                        desktopInfusionCounter={desktopInfusionCounter}
                        mobileInfusionCounter={mobileInfusionCounter}
                    />
                ))}
            </ErrorBoundary>
            {infusions && <Infusions infusions={infusions} />}
        </>
    );
};

const AlternativeProductsGrid: FC<ProductGridProps> = ({ className, products, list, alternativeProducts }) => {
    const t = useBellaTranslator();
    const { skuDiver } = useFeatureToggle();
    if (!alternativeProducts || alternativeProducts?.length <= 0) {
        return null;
    }
    return (
        <>
            {products.length > 0 && <StyledLineSeparator />}
            <Title data-testid="plp-alternative-products-title">{t('Related products')}</Title>
            <StyledGrid data-testid="plp-alternative-products-grid" className={className}>
                {skuDiver && <SkuDiverActionMenu products={alternativeProducts} />}
                <ProductsGrid products={alternativeProducts} list={list} />
            </StyledGrid>
        </>
    );
};

export const PLPGrid: FC<ProductGridProps> = props => {
    const { className, products, list, infusions } = props;
    const { skuDiver } = useFeatureToggle();
    const { looksInfusions } = useBellaSelector(state => state.products);
    return (
        <>
            <StyledGrid data-testid="plp-products-grid" className={className}>
                {skuDiver && <SkuDiverActionMenu products={products} />}
                <PLPProductsGrid
                    products={products}
                    list={list}
                    infusions={infusions}
                    eagerImageLoading
                    looksInfusions={looksInfusions}
                />
            </StyledGrid>
            <AlternativeProductsGrid {...props} />
        </>
    );
};

export const HorizontalGrid: FC<GridBaseProps> = ({ className, products, list, ref }) => (
    <HorizontalGridWrapper className={className} ref={ref} sliderLength={products.length}>
        <ProductsGrid products={products} list={list} isInline />
    </HorizontalGridWrapper>
);

export default PLPGrid;
